import {
  onBeforeMount,
  onBeforeUnmount,
  ref,
  type Ref,
  provide,
  inject,
  type InjectionKey
} from 'vue'
import { emitter } from '@/plugins/emitter'

type CareerHistoryItem = {
  year: number | ''
  month: number | ''
  description: string
  uuid: string
}

export type CareerYagiofferFormatData = {
  createDate: Date | null
  writeDate?: string
  educationalHistories: CareerHistoryItem[]
  jobHistories: CareerHistoryItem[]
  licenseHistories: CareerHistoryItem[]
  commuteDescription: string
  commuteHours: string
  commuteMinutes: string
  spouseNumber: string
  hasSpouse: boolean
  underDutyToSupport: boolean
  reason: string
  reasonNew: string
  request: string
  nowNensyu: string
  kibouNensyu: string
  hopeIndustry?: string
  hopeJobcategory?: string
  gradeYear?: string
  notDisplayIjyo?: boolean
}

const CAREER_YAGIOFFER_FORMAT_DATA_KEY: InjectionKey<CareerYagiofferFormatData> =
  Symbol('careerYagiofferFormatData')

export function provideCareerYagiofferFormatData(
  formatData: CareerYagiofferFormatData
) {
  provide(CAREER_YAGIOFFER_FORMAT_DATA_KEY, formatData)
}

export function useCareerYagiofferFormatData(): CareerYagiofferFormatData {
  const formatData = inject(CAREER_YAGIOFFER_FORMAT_DATA_KEY)
  if (!formatData) {
    throw new Error(
      'useCareerYagiofferFormatData() must be used under CareerYagiofferEdit'
    )
  }
  return formatData
}

type HistoryColumns = {
  year?: number | string
  month?: number | string
  description?: string
}

const DEACTIVATE_HISTORY_ITEM = 'deactivate-history-item'

function uuidv4(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

function createHistoryItem(columns?: HistoryColumns): CareerHistoryItem {
  const cols = columns || {}
  return {
    year: Number(cols.year) || '',
    month: Number(cols.month) || '',
    description: cols.description || '',
    uuid: uuidv4()
  }
}

function addHistory(
  historyList: CareerHistoryItem[],
  index: number | undefined | null,
  columns?: HistoryColumns
) {
  const newHistoryItem = createHistoryItem(columns)
  if (typeof index === 'number') {
    historyList.splice(index, 0, newHistoryItem)
  } else {
    historyList.push(newHistoryItem)
  }
}

function moveHistory(
  historyList: CareerHistoryItem[],
  params: { index: number; to: string }
) {
  const fromIndex = params.index
  const to = params.to
  if (fromIndex === 0 && to === 'up') {
    console.log('first item skip')
    return
  }
  if (fromIndex + 1 === historyList.length && to === 'down') {
    console.log('last item skip')
    return
  }
  if (to === 'up') {
    historyList.splice(
      fromIndex - 1,
      2,
      historyList[fromIndex],
      historyList[fromIndex - 1]
    )
  } else if (to === 'down') {
    historyList.splice(
      fromIndex,
      2,
      historyList[fromIndex + 1],
      historyList[fromIndex]
    )
  } else {
    console.log('param "to" must be "up" or "down"')
  }
}

export function createEmptyCareerYagiofferFormatData(): CareerYagiofferFormatData {
  return {
    createDate: null,
    educationalHistories: [],
    jobHistories: [],
    licenseHistories: [],
    commuteDescription: '',
    commuteHours: '',
    commuteMinutes: '',
    spouseNumber: '',
    hasSpouse: false,
    underDutyToSupport: false,
    reason: '',
    reasonNew: '',
    request: '',
    nowNensyu: '',
    kibouNensyu: '',
    hopeIndustry: '',
    hopeJobcategory: '',
    gradeYear: '',
    notDisplayIjyo: false
  }
}

export function useCareerYagiofferHistories(
  formatData: CareerYagiofferFormatData
): {
  activeHistoryItem: Ref<string>
  educationalResetDialogVisible: Ref<boolean>
  jobResetDialogVisible: Ref<boolean>
  setEducationalBackgrounds: (
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) => void
  setJobHistories: (
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) => void
  resetEducationalBackgrounds: () => void
  resetJobHistories: () => void
  addEducationalHistory: (
    index?: number | null,
    columns?: HistoryColumns
  ) => void
  deleteEducationalHistory: (index: number) => void
  moveEducationalHistory: (params: { index: number; to: string }) => void
  addJobHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteJobHistory: (index: number) => void
  moveJobHistory: (params: { index: number; to: string }) => void
  activateHistoryItem: (id: string) => void
  deactivateHistoryItem: () => void
} {
  const activeHistoryItem = ref('')
  const educationalResetDialogVisible = ref(false)
  const jobResetDialogVisible = ref(false)

  function setEducationalBackgrounds(
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) {
    formatData.educationalHistories = []
    let index = 0
    for (const key in historyList) {
      const item = historyList[key]
      addEducationalHistory(index++, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function setJobHistories(
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) {
    for (const key in historyList) {
      const item = historyList[key]
      addJobHistory(null, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function resetEducationalBackgrounds() {
    formatData.educationalHistories = []
  }

  function resetJobHistories() {
    formatData.jobHistories = []
  }

  function addEducationalHistory(
    index?: number | null,
    columns?: HistoryColumns
  ) {
    if (formatData.educationalHistories == null) {
      formatData.educationalHistories = []
    }
    addHistory(formatData.educationalHistories, index, columns)
  }

  function deleteEducationalHistory(index: number) {
    formatData.educationalHistories.splice(index, 1)
  }

  function moveEducationalHistory(params: { index: number; to: string }) {
    moveHistory(formatData.educationalHistories, params)
  }

  function addJobHistory(index?: number | null, columns?: HistoryColumns) {
    if (formatData.jobHistories == null) {
      formatData.jobHistories = []
    }
    addHistory(formatData.jobHistories, index, columns)
  }

  function deleteJobHistory(index: number) {
    formatData.jobHistories.splice(index, 1)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistory(formatData.jobHistories, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    educationalResetDialogVisible,
    jobResetDialogVisible,
    setEducationalBackgrounds,
    setJobHistories,
    resetEducationalBackgrounds,
    resetJobHistories,
    addEducationalHistory,
    deleteEducationalHistory,
    moveEducationalHistory,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
